import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { Labels } from '../../global/labels';
import { element, html, trustedHTML } from '../../global/template-literals';
import { getDebugMode } from '../../global/config';
import type { UIMeterBarLevels } from './ui-meterbar.type';
import type { TranslationLabels } from '../../types/labels.type';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import type { NullOr } from '../../global/utils.type';
import styles from './ui-meterbar.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIMeterBar
 * @element ui-meterbar
 * @classdesc Represents a class for <code>ui-meterbar</code> element.
 * Element to display meter.
 * @property {string} [labelMin] {@attr label-min} Label for low level.
 * @property {string} [labelMid] {@attr label-mid} Label for medium level.
 * @property {string} [labelMax] {@attr label-max} Label for high level.
 * @property {string} [labelLevel] {@attr label-level} Label for level, used in accessibility only.
 * @property {string} [unit] {@attr unit} Scale unit.
 * @property {number} [min=0] {@attr min} The minimum level.
 * @property {number} [max=7] {@attr max} The maximum level.
 * @property {number} [value=4] {@attr value} The current value of the level.
 * @property {number} [step=1] {@attr step} The step of the scale.
 * @property {boolean} [noscale=false] {@attr noscale} Do not render the scale.
 * @property {boolean} [trusted=false] {@attr trusted} Defines if element's strings are
 * trusted HTML.
 * @property {HTMLDivElement} [gradesElem] Shortcut to grades container element.
 * @property {HTMLDivElement} [labelsElem] Shortcut to labels container element.
 * @slot
 * @example
 * <ui-meterbar min="0" max="7" value="4" step="1"></ui-meterbar>
 */
@Component({ tag: 'ui-meterbar', styles })
export class UIMeterBar extends UIElement {
    /**
     * @type {UIMeterBarLevels}
     * @readonly
     */
    static readonly levels: UIMeterBarLevels = {
        LOW: Math.ceil((1 / 3) * 100),
        HIGH: Math.ceil((2 / 3) * 100),
        MAX: 100,
    };

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-meterbar', {
            labelMin: 'Low',
            labelMax: 'High',
            level: 'Level',
        });
    }

    /**
     * @type {Array<string>}
     * @readonly
     */
    static get observedAttributes(): ObservedAttributes {
        return [
            'min',
            'max',
            'value',
            'step',
            'unit',
            'label-min',
            'label-mid',
            'label-max',
            'noscale',
        ];
    }

    @Attribute(Number) min: number = 0;
    @Attribute(Number) max: number = 7;
    @Attribute(Number) value: number = 4;
    @Attribute(Number) step: number = 1;
    @Attribute(String) unit: string = '';
    @Attribute(String) labelMin?: string;
    @Attribute(String) labelMid?: string;
    @Attribute(String) labelMax?: string;
    @Attribute(String) labelLevel?: string;
    @Attribute(Boolean) noscale?: boolean = false;
    @Attribute(Boolean) trusted?: boolean = false;

    @Child('.ui-meterbar__labels') labelsElem?: HTMLDivElement;
    @Child('.ui-meterbar__scale') gradesElem?: HTMLDivElement;

    /**
     * Gets the scale points step.
     * @type {number}
     * @readonly
     */
    get getStep() {
        return this.step || 1;
    }

    /**
     * Gets the scale points array.
     * @type {Array<string>}
     * @readonly
     */
    get scalePoints(): Array<string> {
        const scalePoints: Array<string> = [];
        const step = this.getStep;
        for (
            let scalePoint = this.min;
            scalePoint <= this.max;
            scalePoint += step
        ) {
            scalePoints.push(`${scalePoint}${this.unit}`);
        }
        return scalePoints;
    }

    /**
     * Calculates the position of the meter in percent.
     * @returns {number}
     */
    calculatePositionPct(): number {
        if (this.max === this.min) {
            return 0;
        }
        return ((this.value - this.min) / (this.max - this.min)) * 100;
    }

    /**
     * Updates the position of the meter.
     */
    updatePosition(): void {
        if (this.min > this.max) {
            getDebugMode() &&
                console.warn(
                    `${this.localName}: minimum value is larger than maximum.`
                );
        }
        if (this.value > this.max) {
            getDebugMode() &&
                console.warn(
                    `${this.localName}: the value is larger than maximum.`
                );
            this.value = this.max;
        }
        let color: string;
        let pos = this.calculatePositionPct();
        if (pos < UIMeterBar.levels.LOW) {
            color = '--ui-color-alert-green';
        } else if (
            pos >= UIMeterBar.levels.LOW &&
            pos < UIMeterBar.levels.HIGH
        ) {
            color = '--ui-color-pineapple';
        } else {
            color = '--ui-color-orange';
        }

        if (pos > UIMeterBar.levels.MAX) {
            // Position should not be larger than 100%.
            pos = UIMeterBar.levels.MAX;
        } else if (pos < 0) {
            pos = 0;
        }
        this.style.setProperty('--ui-meterbar-color', `var(${color})`);
        this.style.setProperty('--ui-meterbar-position', `${Math.round(pos)}%`);
    }

    /**
     * Builds grades elements.
     * @private
     * @returns {Array<Node>}
     */
    private buildScale(): Array<Node> {
        return this.scalePoints.map(
            (scalePoint) =>
                element`<span class="ui-meterbar__scale-point">${scalePoint}</span>`
        );
    }

    /**
     * Build labels elements.
     * @private
     * @returns {DocumentFragment}
     */
    private buildLabels(): DocumentFragment {
        const renderer = this.trusted ? trustedHTML : html;
        return renderer`
        <span>${this.labelMin || this.getLabel('labelMin')}</span>
        ${this.labelMid && renderer`<span>${this.labelMid}</span>`}        
        <span>${this.labelMax || this.getLabel('labelMax')}</span>`;
    }

    /**
     * Builds content for the ui-meterbar.
     * @private
     * @returns {DocumentFragment}
     */
    private buildContent(): DocumentFragment {
        return html` <div class="ui-meterbar__labels">
                ${this.buildLabels()}
            </div>
            <div class="ui-meterbar__meter"></div>
            <div class="ui-meterbar__scale">
                ${!this.noscale ? this.buildScale() : ''}
            </div>`;
    }

    /**
     * Applies accessibility ot meter.
     * @private
     */
    private applyA11y(): void {
        this.setAttribute('role', 'meter');
        this.setAttribute('aria-valuemin', `${String(this.min)}${this.unit}`);
        this.setAttribute('aria-valuemax', `${String(this.max)}${this.unit}`);
        this.setAttribute('aria-valuenow', `${String(this.value)}${this.unit}`);
        this.setAttribute(
            'aria-valuetext',
            `${this.labelLevel || this.getLabel('level')} ${this.value}${
                this.unit
            }`
        );
        this.setAttribute('aria-live', 'polite');
    }

    /**
     * Sets the value. This method exists for API consistency.
     * @param {string|number} value
     */
    setValue(value: string | number): void {
        this.value = Number(value);
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        _name: AttributeValue,
        _oldValue: AttributeValue,
        _newValue: NullOr<string>
    ): void {
        this.updatePosition();
        this.applyA11y();
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.rebuildChildren([this.buildContent()]);
        this.updatePosition();
        this.applyA11y();
    }
}
